import { useEffect, useState } from "react";
import { Search, Eye, Trash2, Mail, Phone, User } from "lucide-react";
import { GetAllQueries, DeleteQuery } from "../api";

function Query() {
  const [queries, setQueries] = useState([]);
  const [search, setSearch] = useState("");
  const [selectedQuery, setSelectedQuery] = useState(null);

  useEffect(() => {
    fetchQueries();
  }, []);

  const fetchQueries = async () => {
    try {
      const res = await GetAllQueries();
      setQueries(res.data.data);
    } catch (error) {
      console.log(error);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this query?")) return;

    try {
      await DeleteQuery(id);
      fetchQueries();
    } catch (error) {
      console.log(error);
    }
  };

  const filteredQueries = queries.filter(
    (item) =>
      item.name.toLowerCase().includes(search.toLowerCase()) ||
      item.email.toLowerCase().includes(search.toLowerCase()) ||
      item.purpose.toLowerCase().includes(search.toLowerCase()),
  );

  return (
    <div className="p-6 bg-gray-100 min-h-screen">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-5 mb-6">
        {/* Title */}
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-800">
            Contact Queries
          </h1>

          <p className="text-sm sm:text-base text-gray-500 mt-1">
            Manage all customer enquiries
          </p>
        </div>

        {/* Search */}
        <div className="relative w-full lg:w-80">
          <Search
            size={18}
            className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400"
          />

          <input
            type="text"
            placeholder="Search Query..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full h-12 rounded-xl border border-gray-300 pl-11 pr-4 text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
          />
        </div>
      </div>

      {/* Stats */}
      <div className="grid md:grid-cols-3 gap-5 mb-8">
        <div className="bg-white rounded-xl shadow p-5 flex items-center gap-4">
          <div className="bg-green-100 p-3 rounded-full">
            <Mail className="text-green-600" />
          </div>

          <div>
            <h3 className="text-gray-500">Total Queries</h3>
            <h2 className="text-2xl font-bold">{filteredQueries.length}</h2>
          </div>
        </div>
      </div>

      {/* Table */}
     <div className="bg-white rounded-xl shadow overflow-hidden">

  {/* Desktop Table */}
  <div className="hidden md:block overflow-x-auto">
    <table className="w-full min-w-[900px]">
      <thead className="bg-gray-50">
        <tr className="text-left text-gray-600 text-sm">
          <th className="p-4">User</th>
          <th className="p-4">Purpose</th>
          <th className="p-4">Message</th>
          <th className="p-4">Date</th>
          <th className="p-4 text-center">Action</th>
        </tr>
      </thead>

      <tbody>
        {filteredQueries.length > 0 ? (
          filteredQueries.map((item) => (
            <tr
              key={item._id}
              className="border-t hover:bg-gray-50 transition"
            >
              <td className="p-4">
                <div className="font-semibold flex items-center gap-2">
                  <User size={16} />
                  {item.name}
                </div>

                <div className="text-sm text-gray-500 flex items-center gap-2 mt-1">
                  <Mail size={14} />
                  {item.email}
                </div>

                <div className="text-sm text-gray-500 flex items-center gap-2 mt-1">
                  <Phone size={14} />
                  {item.number}
                </div>
              </td>

              <td className="p-4">{item.purpose}</td>

              <td className="p-4 max-w-xs truncate">
                {item.message || "-"}
              </td>

              <td className="p-4 whitespace-nowrap">
                {new Date(item.createdAt).toLocaleDateString()}
              </td>

              <td className="p-4">
                <div className="flex justify-center gap-3">
                  <button
                    onClick={() => setSelectedQuery(item)}
                    className="p-2 rounded-lg bg-blue-50 hover:bg-blue-100 text-blue-600"
                  >
                    <Eye size={18} />
                  </button>

                  <button
                    onClick={() => handleDelete(item._id)}
                    className="p-2 rounded-lg bg-red-50 hover:bg-red-100 text-red-600"
                  >
                    <Trash2 size={18} />
                  </button>
                </div>
              </td>
            </tr>
          ))
        ) : (
          <tr>
            <td
              colSpan={5}
              className="text-center py-10 text-gray-500"
            >
              No Queries Found
            </td>
          </tr>
        )}
      </tbody>
    </table>
  </div>

  {/* Mobile Cards */}
  <div className="md:hidden p-4 space-y-4">
    {filteredQueries.length > 0 ? (
      filteredQueries.map((item) => (
        <div
          key={item._id}
          className="border rounded-xl p-4 shadow-sm"
        >
          <div className="flex items-center gap-2 font-semibold text-lg">
            <User size={18} />
            {item.name}
          </div>

          <div className="flex items-center gap-2 text-sm text-gray-500 mt-2">
            <Mail size={15} />
            {item.email}
          </div>

          <div className="flex items-center gap-2 text-sm text-gray-500 mt-1">
            <Phone size={15} />
            {item.number}
          </div>

          <div className="mt-4">
            <p className="text-xs text-gray-500">Purpose</p>
            <p className="font-medium">{item.purpose}</p>
          </div>

          <div className="mt-3">
            <p className="text-xs text-gray-500">Message</p>
            <p className="text-sm">
              {item.message || "-"}
            </p>
          </div>

          <div className="mt-3">
            <p className="text-xs text-gray-500">Date</p>
            <p className="text-sm">
              {new Date(item.createdAt).toLocaleDateString()}
            </p>
          </div>

          <div className="flex gap-3 mt-5">
            <button
              onClick={() => setSelectedQuery(item)}
              className="flex-1 flex justify-center items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white py-2 rounded-lg"
            >
              <Eye size={18} />
              View
            </button>

            <button
              onClick={() => handleDelete(item._id)}
              className="flex-1 flex justify-center items-center gap-2 bg-red-600 hover:bg-red-700 text-white py-2 rounded-lg"
            >
              <Trash2 size={18} />
              Delete
            </button>
          </div>
        </div>
      ))
    ) : (
      <div className="text-center py-10 text-gray-500">
        No Queries Found
      </div>
    )}
  </div>
</div>
    {selectedQuery && (
  <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
    <div className="bg-white w-full max-w-2xl rounded-2xl shadow-2xl overflow-hidden max-h-[90vh] overflow-y-auto">

      {/* Header */}
      <div className="bg-gradient-to-r from-green-600 to-emerald-500 px-5 sm:px-6 py-5 text-white flex items-start justify-between">
        <div>
          <h2 className="text-xl sm:text-2xl font-bold">
            Query Details
          </h2>

          <p className="text-green-100 text-sm mt-1">
            Customer enquiry information
          </p>
        </div>

        <button
          onClick={() => setSelectedQuery(null)}
          className="text-2xl font-bold hover:opacity-80"
        >
          ✕
        </button>
      </div>

      {/* Body */}
      <div className="p-5 sm:p-6">

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

          <div className="bg-gray-50 rounded-xl border p-2 md:p-4">
            <p className="text-sm text-gray-500 mb-1">
              Name
            </p>

            <h3 className="font-semibold text-gray-800 break-words">
              {selectedQuery.name}
            </h3>
          </div>

          <div className="bg-gray-50 rounded-xl border p-2 md:p-4">
            <p className="text-sm text-gray-500 mb-1">
              Email
            </p>

            <h3 className="font-semibold text-gray-800 break-all">
              {selectedQuery.email}
            </h3>
          </div>

          <div className="bg-gray-50 rounded-xl border p-2 md:p-4">
            <p className="text-sm text-gray-500 mb-1">
              Phone
            </p>

            <h3 className="font-semibold">
              {selectedQuery.number}
            </h3>
          </div>

          <div className="bg-green-50 border border-green-200 rounded-xl p-4">
            <p className="text-sm text-gray-500 mb-1">
              Purpose
            </p>

            <h3 className="font-semibold text-green-700">
              {selectedQuery.purpose}
            </h3>
          </div>

          <div className="md:col-span-2 bg-blue-50 border border-blue-200 rounded-xl p-4">
            <p className="text-sm text-gray-500 mb-2">
              Message
            </p>

            <p className="text-gray-800 whitespace-pre-wrap break-words">
              {selectedQuery.message || "No message provided"}
            </p>
          </div>

          <div className="md:col-span-2 bg-gray-50 rounded-xl border p-2 md:p-4">
            <p className="text-sm text-gray-500 mb-1">
              Submitted On
            </p>

            <h3 className="font-medium">
              {new Date(selectedQuery.createdAt).toLocaleString()}
            </h3>
          </div>

        </div>

      </div>

      {/* Footer */}
      <div className="border-t bg-gray-50 px-5 sm:px-6 py-4">
        <button
          onClick={() => setSelectedQuery(null)}
          className="w-full sm:w-auto sm:px-8 py-3 bg-green-600 hover:bg-green-700 text-white rounded-xl font-semibold transition"
        >
          Close
        </button>
      </div>

    </div>
  </div>
)}
    </div>
  );
}

export default Query;
